import { useCallback, useEffect, useMemo, useState } from 'react';
import { Button, Dialog, Input } from '@/design-system';
import { snapshotReasonLabel } from '@/domain/history/snapshot-policy';
import type { HistoryResponse, SnapshotDetail, SnapshotSummary } from '@/domain/history/contracts';
import { normalizeEditorGraph } from '@/features/funnel-editor/domain/normalize-graph';
import { evaluateGraphMetrics } from '@/features/funnel-editor/metrics/evaluate-graph-metrics';
import { computeGraphEmphasis } from '@/domain/presentation/emphasis';
import { toPresentationGraphInput } from '@/features/funnel-editor/presentation/graph-bridge';
import { PresentationCanvas } from '@/features/presentation/PresentationCanvas';
import * as api from './api/history-api';

export function HistoryPanel({
  funnelId,
  currentRevision,
  flush,
  onRestored,
  onClose,
}: {
  funnelId: string;
  currentRevision: number;
  flush: () => Promise<boolean>;
  onRestored: () => Promise<void>;
  onClose: () => void;
}) {
  const [history, setHistory] = useState<HistoryResponse | null>(null);
  const [selected, setSelected] = useState<SnapshotDetail | null>(null);
  const [label, setLabel] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [restoreTarget, setRestoreTarget] = useState<SnapshotSummary | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<SnapshotSummary | null>(null);
  const [renameTarget, setRenameTarget] = useState<SnapshotSummary | null>(null);
  const [renameLabel, setRenameLabel] = useState('');

  const load = useCallback(async () => {
    setHistory(await api.listHistory(funnelId));
  }, [funnelId]);
  useEffect(() => {
    void load().catch((reason) => setError(reason instanceof Error ? reason.message : 'Não foi possível carregar o histórico.'));
  }, [load]);

  const preview = useMemo(() => (selected ? normalizeEditorGraph(selected.graph) : null), [selected]);
  const emphasis = useMemo(
    () => (preview ? computeGraphEmphasis(toPresentationGraphInput(preview), {}) : null),
    [preview],
  );
  const metrics = useMemo(() => (preview ? evaluateGraphMetrics(preview) : null), [preview]);

  async function saveVersion() {
    setBusy(true);
    setError(null);
    try {
      if (!(await flush())) throw new Error('Salve as últimas alterações do editor antes de criar uma versão.');
      await api.createVersion(funnelId, label.trim() || null);
      setLabel('');
      await load();
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : 'Não foi possível salvar a versão.');
    } finally {
      setBusy(false);
    }
  }

  async function openPreview(snapshot: SnapshotSummary) {
    setError(null);
    try {
      setSelected((await api.getVersion(funnelId, snapshot.id)).snapshot);
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : 'A prévia da versão falhou.');
    }
  }

  async function patch(snapshot: SnapshotSummary, values: { label?: string | null; isPinned?: boolean }) {
    await api.updateVersion(funnelId, snapshot.id, values);
    await load();
  }

  return (
    <aside aria-label="Histórico de versões" className="w-[420px] shrink-0 overflow-y-auto border-l border-border-subtle bg-surface p-4">
      <div className="mb-4 flex items-center justify-between">
        <h2 className="font-semibold">Histórico de versões</h2>
        <button type="button" aria-label="Fechar histórico" onClick={onClose}>×</button>
      </div>
      <div className="mb-4 rounded border border-border-subtle p-3">
        <label className="text-xs text-text-secondary">
          Rótulo da versão (opcional)
          <Input value={label} maxLength={120} onChange={(event) => setLabel(event.target.value)} />
        </label>
        <Button className="mt-2 w-full" disabled={busy} onClick={() => void saveVersion()}>
          {busy ? 'Salvando…' : 'Salvar versão'}
        </Button>
      </div>
      {error ? <p role="alert" className="mb-3 text-sm text-[#f08484]">{error}</p> : null}
      {history ? (
        <ol className="space-y-2" aria-label="Versões salvas">
          <li className="rounded border border-border-brand p-3">
            <strong className="text-sm">Versão atual</strong>
            <p className="text-xs text-text-muted">Revisão {history.current.revision} · {history.current.nodeCount} nós · {history.current.edgeCount} conexões</p>
          </li>
          {history.snapshots.map((snapshot) => (
            <li key={snapshot.id} className="rounded border border-border-subtle p-3">
              <div className="flex justify-between gap-2">
                <div>
                  <strong className="text-sm">{snapshot.label || snapshotReasonLabel(snapshot.reason)}</strong>
                  <p className="text-xs text-text-muted">Revisão {snapshot.sourceRevision} · {new Date(snapshot.createdAt).toLocaleString('pt-BR')}</p>
                </div>
                {snapshot.isPinned ? <span aria-label="Fixada" title="Fixada">★</span> : null}
              </div>
              <p className="mt-1 text-xs text-text-muted">{snapshot.nodeCount} nós · {snapshot.edgeCount} conexões</p>
              <div className="mt-2 flex flex-wrap gap-2">
                <Button variant="secondary" onClick={() => void openPreview(snapshot)}>Prévia</Button>
                <Button variant="secondary" onClick={() => void patch(snapshot, { isPinned: !snapshot.isPinned })}>
                  {snapshot.isPinned ? 'Desafixar' : 'Fixar'}
                </Button>
                <Button variant="secondary" onClick={() => {
                  setRenameTarget(snapshot);
                  setRenameLabel(snapshot.label ?? '');
                }}>Renomear</Button>
                <Button variant="secondary" onClick={() => setRestoreTarget(snapshot)}>Restaurar</Button>
                <Button variant="danger" onClick={() => setDeleteTarget(snapshot)}>Excluir</Button>
              </div>
            </li>
          ))}
        </ol>
      ) : <p role="status">Carregando histórico…</p>}

      {preview && emphasis && selected ? (
        <section aria-label="Prévia da versão" className="mt-4 rounded border border-border-subtle p-2">
          <h3 className="mb-2 text-sm font-medium">Prévia somente leitura · revisão {selected.sourceRevision}</h3>
          <div className="h-56 overflow-hidden rounded">
            <PresentationCanvas nodes={preview.nodes} edges={preview.edges} emphasis={emphasis} />
          </div>
          <p className="mt-2 text-xs text-text-muted">
            Métricas recalculadas · receita {metrics?.funnel.revenue.status ?? 'desconhecido'}
          </p>
          {selected.warnings.map((warning) => <p key={warning} className="text-xs text-[#e0b866]">{warning}</p>)}
        </section>
      ) : null}

      <Dialog open={restoreTarget !== null} onClose={() => setRestoreTarget(null)} title="Restaurar versão">
        <p className="mb-4 text-sm text-text-secondary">O conteúdo selecionado substituirá o grafo ativo como uma nova revisão. Um ponto de segurança será criado antes.</p>
        <div className="flex justify-end gap-2">
          <Button variant="secondary" onClick={() => setRestoreTarget(null)}>Cancelar</Button>
          <Button onClick={() => void (async () => {
            if (!restoreTarget) return;
            try {
              await api.restoreVersion(funnelId, restoreTarget.id, currentRevision);
              await onRestored();
              setRestoreTarget(null);
              await load();
            } catch (reason) {
              setError(reason instanceof Error ? reason.message : 'A restauração falhou.');
              setRestoreTarget(null);
            }
          })()}>Confirmar restauração</Button>
        </div>
      </Dialog>

      <Dialog open={renameTarget !== null} onClose={() => setRenameTarget(null)} title="Renomear versão salva">
        <label className="text-sm text-text-secondary">
          Rótulo da versão
          <Input className="mt-1" aria-label="Rótulo da versão" maxLength={120} value={renameLabel} onChange={(event) => setRenameLabel(event.target.value)} />
        </label>
        <div className="mt-4 flex justify-end gap-2">
          <Button variant="secondary" onClick={() => setRenameTarget(null)}>Cancelar</Button>
          <Button onClick={() => void (async () => {
            if (!renameTarget) return;
            await patch(renameTarget, { label: renameLabel.trim() || null });
            setRenameTarget(null);
          })()}>Salvar rótulo</Button>
        </div>
      </Dialog>

      <Dialog open={deleteTarget !== null} onClose={() => setDeleteTarget(null)} title="Excluir versão salva">
        <p className="mb-4 text-sm text-text-secondary">Excluir este ponto de controle não altera o funil atual.</p>
        <div className="flex justify-end gap-2">
          <Button variant="secondary" onClick={() => setDeleteTarget(null)}>Cancelar</Button>
          <Button variant="danger" onClick={() => void (async () => {
            if (!deleteTarget) return;
            await api.deleteVersion(funnelId, deleteTarget.id);
            if (selected?.id === deleteTarget.id) setSelected(null);
            setDeleteTarget(null);
            await load();
          })()}>Excluir versão</Button>
        </div>
      </Dialog>
    </aside>
  );
}
